import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { fmtMoney } from '../../utils/money';
import PaymentBadge from '../../components/financial/PaymentBadge';
import ProjectSummaryTable from '../../components/financial/ProjectSummaryTable';
import TransfersPanel from '../../components/financial/TransfersPanel';
import ContractCard from '../../components/financial/ContractCard';

const API = process.env.REACT_APP_API || '/api';

// تفصيل مشروع في بوابة العرض العامة — مالي + رابط التقرير الفني.
export default function FinancialPortalProject() {
  const { slug, projectId } = useParams();
  const [params] = useSearchParams();
  const month = params.get('month') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = {};
      const pwd = sessionStorage.getItem(`fin-portal-pwd-${slug}`);
      if (pwd) headers['X-Financial-Password'] = pwd;
      const qs = month ? `?month=${month}` : '';
      const res = await fetch(`${API}/financial/portal/${slug}/projects/${projectId}${qs}`, { headers });
      const json = await res.json();
      if (!res.ok) { setError(json.error || 'تعذّر تحميل التقرير'); setData(null); }
      else setData(json);
    } catch {
      setError('فشل التحميل');
    } finally {
      setLoading(false);
    }
  }, [slug, projectId, month]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={wrap}><div className="loader">جاري التحميل...</div></div>;
  if (error) return <div style={wrap}><div className="empty">{error}</div></div>;
  if (!data) return <div style={wrap}><div className="empty">لا توجد بيانات</div></div>;

  const p = data.project;
  const pending = p.funding_status === 'approved_pending';
  const backLink = `/financial/${slug}${month ? `?month=${month}` : ''}`;

  return (
    <div style={wrap} dir="rtl">
      <Link to={backLink} style={{ fontSize: 13, color: 'var(--text-muted)', display: 'inline-block', marginBottom: 8 }}>← كل المشاريع</Link>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>{p.name}</div>
            {p.unit_basis && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>وحدة القياس: {p.unit_basis}</div>}
          </div>
          {p.technical_url && <a className="btn btn-primary btn-sm" href={p.technical_url} target="_blank" rel="noreferrer">📋 التقرير الفني ↗</a>}
        </div>

        {pending ? (
          <div style={{ marginTop: 12, display: 'flex', gap: 16, alignItems: 'center' }}>
            <Stat label="المبلغ المعتمد" value={fmtMoney(p.approved_amount)} />
            <PaymentBadge status={p.payment_status} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>معتمد بانتظار التمويل.</span>
          </div>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, alignItems: 'center' }}>
            <Stat label="إجمالي التمويل" value={fmtMoney(p.funding_total)} />
            <Stat label="المستهلَك" value={fmtMoney(p.total_used)} />
            <Stat label="المتبقّي" value={fmtMoney(p.total_remaining)} />
            <Stat label="المدفوع" value={fmtMoney(p.paid_amount)} />
            <div style={{ textAlign: 'center' }}><PaymentBadge status={p.payment_status} /></div>
          </div>
        )}
      </div>

      {!pending && (
        <>
          <ProjectSummaryTable items={p.summary_items} />
          <TransfersPanel transfers={p.transfers} />
          <div className="card-title" style={{ marginBottom: 12 }}>العقود ({p.contracts.length})</div>
          {p.contracts.map((c) => <ContractCard key={c.id} contract={c} />)}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ textAlign: 'center', padding: 8 }}>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}

const wrap = { maxWidth: 1000, margin: '0 auto', padding: 20 };
